import React, { useState, useCallback, useEffect, useMemo } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  AddOutlined,
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

// Services
import servicetypeapi from "Services/ZoneServicefare";

// Modals
import ServicetypeAddModal from "Pages/Modals/ServicetypeZonefare/Create";
import ServicetypeUpdateModal from "Pages/Modals/ServicetypeZonefare/Update";
import ServicetypeDeleteDialog from "Pages/Modals/ServicetypeZonefare/Delete";

const ServicetypeZonefare = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [zonedata, setzonedata] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [addServicetypeModal, setaddServicetypeModal] = useState(false);
  const [updateServicetypeModal, setupdateServicetypeModal] = useState(false);
  const [deleteServicetypeModal, setdeleteServicetypeModal] = useState(false);

  const handleOpenAddServiceModal = () => {
    setaddServicetypeModal(true);
  };

  const handleCloseAddServiceModal = () => {
    setaddServicetypeModal(false);
    getServicetype();
  };

  const handleOpenUpdateServiceModal = (id) => {
    setCustomerId(id);
    setupdateServicetypeModal(true);
  };

  const handleCloseUpdateServiceModal = () => {
    setupdateServicetypeModal(false);
    getServicetype();
  };

  const handleOpenDeleteServiceModal = (id) => {
    setCustomerId(id);
    setdeleteServicetypeModal(true);
  };

  const handleCloseDeleteServiceModal = () => {
    setdeleteServicetypeModal(false);
    getServicetype();
  };

  // Get Services list api
  const getServicetype = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data || []);
        setzonedata(res?.data?.data?.zonefaredata || []);
      } else {
        setRow([]);
        setzonedata([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    servicetypeapi
      .getzoneServicesList({ page, limit: pageSize, demoaccount_id, search: "" })
      .then(onSuccess, onFailure);
  }, [page, pageSize, demoaccount_id, dispatch]);

  useEffect(() => {
    getServicetype();
  }, [getServicetype]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = useMemo(() => {
    let filteredData = zonedata || [];
    if (searchText.trim()) {
      filteredData = filteredData.filter(
        (item) =>
          item?.SourceAddress?.locationName?.toLowerCase().includes(searchText.toLowerCase()) ||
          item?.DestinationAddress?.locationName?.toLowerCase().includes(searchText.toLowerCase()) ||
          item?.zoneserviceTypeId?.serviceName?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    return _.map(filteredData, (data, index) => ({
      sno: calculateSno(page, pageSize, index),
      _id: data?._id,
      sourceAddress: data?.SourceAddress?.locationName || "-",
      destination: data?.DestinationAddress?.locationName || "-",
      reverse: data?.ReverseLocation ? "YES" : "NO",
      serviceType: data?.zoneserviceTypeId?.serviceName || "-",
      status: data?.isActive ?? false,
      row: data,
    }));
  }, [zonedata, page, pageSize, searchText]);

  // Pagination
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Service Zone Fares</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Service Zone Fares</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search zone fares..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        {/* Right Actions */}
        <Box className={classes.rightActions}>
          {!demoaccount_id && (
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={handleOpenAddServiceModal}
              startIcon={<AddOutlined />}
            >
              Add Zone-Wise Fare
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Source Address</TableCell>
              <TableCell className={classes.tableHeaderCell}>Destination Address</TableCell>
              <TableCell className={classes.tableHeaderCell}>Reverse</TableCell>
              <TableCell className={classes.tableHeaderCell}>Service Type</TableCell>
              <TableCell className={classes.tableHeaderCell}>Status</TableCell>
              {!demoaccount_id && (
                <TableCell className={classes.tableHeaderCell} align="right">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.sourceAddress}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.destination}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.reverse}</TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.serviceType}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box
                    sx={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: "100px",
                      fontSize: "12px",
                      fontWeight: "600",
                      textTransform: "capitalize",
                      backgroundColor: rowItem.status ? "#dcfce7" : "#fee2e2",
                      color: rowItem.status ? "#15803d" : "#b91c1c",
                    }}
                  >
                    {rowItem.status ? "Active" : "Inactive"}
                  </Box>
                </TableCell>
                {!demoaccount_id && (
                  <TableCell className={classes.tableCell} align="right">
                    <Box
                      component="span"
                      className={classes.actionButton}
                      onClick={() => handleOpenUpdateServiceModal(rowItem._id)}
                    >
                      <EditOutlined fontSize="small" />
                      Edit
                    </Box>
                    <Box
                      component="span"
                      className={classes.actionDeleteButton}
                      onClick={() => handleOpenDeleteServiceModal(rowItem._id)}
                    >
                      <DeleteOutline fontSize="small" />
                      Delete
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={demoaccount_id ? 6 : 7}
                  style={{ textAlign: "center", padding: "40px" }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No zone fares found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>

      <ServicetypeAddModal
        openDialog={addServicetypeModal}
        handleClose={handleCloseAddServiceModal}
      />
      <ServicetypeUpdateModal
        openDialog={updateServicetypeModal}
        id={customerId}
        handleClose={handleCloseUpdateServiceModal}
      />
      <ServicetypeDeleteDialog
        openDialog={deleteServicetypeModal}
        id={customerId}
        handleClose={handleCloseDeleteServiceModal}
      />
    </Box>
  );
};

export default ServicetypeZonefare;
